import { Component, Inject, OnDestroy } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { RouterLink } from '@angular/router';

import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { environment } from 'src/environments/environment';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { UserImageComponent } from '../user-image/user-image.component';
import { GetUserForAdminDto } from 'src/app/data-transfer-objects/get-user-for-admin.dto';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PartialEventWithUserInfoDto } from 'src/app/data-transfer-objects/partial-event-with-user-info.dto';
import { ThemePalette } from '@angular/material/core';
import { Observable, SubscriptionLike } from 'rxjs';
import { QRCodeModule } from 'angularx-qrcode';
import { AccountBlockService } from 'src/app/services/account-block.service';
import { UsersService } from 'src/app/services/users-service.service';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import {
  ChatSenderDto,
  OpenerDto,
  TEAM_OPENERS,
  TeamChatStateDto,
} from 'src/app/data-transfer-objects/team-chat.dto';
import { SharonPanelComponent } from './sharon-panel.component';
dayjs.extend(relativeTime);

@Component({
  selector: 'app-user-info-dialog',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatProgressBarModule,
    MatSelectModule,
    FormsModule,
    UserImageComponent,
    QRCodeModule,
    SharonPanelComponent,
  ],
  templateUrl: './user-info-dialog.component.html',
  styleUrls: ['./user-info-dialog.component.scss'],
})
export class UserInfoDialogComponent implements OnDestroy {
  private locationSubscription: SubscriptionLike;
  googleMapsIframeSrc = this.getGoogleMapsIframeSrc();

  userProfileUrlForQRCode?: string;

  mapLoaded = false;

  /** "Open to" as words, worked out once rather than on every check. */
  openTo: string[] = [];
  /** Briefly true after the ID was copied, for the button to say so. */
  idCopied = false;

  /*
   * Block from here, not only from the case page. An account is often
   * given away by its profile - a scam opener pasted into the role field -
   * before the guard has counted anything, and this dialog is where an admin
   * is looking when they notice. Every board opens it, so every board gets
   * the button.
   */
  /** 'block' | 'unblock' while a request or its confirm dialog is open. */
  busy: string | null = null;
  actionError: string | null = null;
  actionNotice: string | null = null;

  /*
   * Messaging a member as the NX2U Team, or as another account the admin
   * picks from the senders list (the server decides who is on it).
   *
   * Here rather than on the Members board because this dialog is what every
   * board opens when you want to know who somebody is, and deciding to talk
   * to them is the next thought after reading their profile.
   *
   * The panel is closed until asked for: this writes a real message to a real
   * person and cannot be undone, so it should not be one stray click away
   * from a dialog people open all day to read profiles.
   */
  composerOpen = false;
  teamChat: TeamChatStateDto | null = null;
  teamChatError: string | null = null;
  senders: ChatSenderDto[] = [];
  senderUid: string | null = null;
  openers: OpenerDto[] = TEAM_OPENERS;
  selectedOpener: string | null = null;
  messageText = '';
  sending = false;
  sentNotice: string | null = null;

  /** The Sharon section: her conversation with this member, or a way to start one. */
  sharonOpen = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public user: GetUserForAdminDto,
    private matDialog: MatDialog,
    private location: Location,
    private domSanitizer: DomSanitizer,
    private accountBlock: AccountBlockService,
    private usersService: UsersService
  ) {
    this.openTo = (this.user.freeFor ?? [])
      .map((v) => this.getfreeForText(v))
      .filter((label) => label !== 'N/A');

    // Push state to the history stack
    this.location.go(this.location.path());

    this.locationSubscription = this.location.subscribe(() => {
      this.matDialog.closeAll();
      // Prevent the default action (going back)
      this.location.replaceState(this.location.path());
    });

    // A row from the users list does not say whether the account is locked
    // out; the single-user read does. Until it answers, neither button shows.
    if (this.user.blocked === undefined) {
      this.usersService.getUserForAdmin(this.user.id).subscribe({
        next: (fresh) => (this.user.blocked = fresh.blocked ?? false),
        error: () => (this.actionError = 'Could not tell whether this account is blocked.'),
      });
    }
  }

  /** Open the composer and find out whether a conversation already exists. */
  openComposer(): void {
    this.composerOpen = true;
    this.sentNotice = null;
    this.teamChatError = null;
    if (this.teamChat) return;
    this.loadTeamChat();
  }

  /** The account the message goes out as. */
  get sender(): ChatSenderDto | undefined {
    return this.senders.find((s) => s.uid === this.senderUid);
  }

  /** A sender's name for the contact log, or nothing for one no longer listed. */
  senderLabel(uid: string | undefined): string | null {
    return this.senders.find((s) => s.uid === uid)?.label ?? null;
  }

  /*
   * Switching sender means a different chat - the member may have talked to
   * one account and never the other - so the state is read again. An opener
   * left untouched is re-signed with the new name.
   */
  chooseSender(uid: string): void {
    if (uid === this.senderUid) return;
    const untouched = this.selectedOpener;
    this.senderUid = uid;
    this.teamChat = null;
    this.sentNotice = null;
    if (untouched) this.applyOpener(untouched);
    this.loadTeamChat();
  }

  private loadTeamChat(): void {
    this.teamChatError = null;
    this.usersService.getTeamChatState(this.user.id, this.senderUid ?? undefined).subscribe({
      next: (state) => {
        this.teamChat = state;
        this.senders = state.senders ?? [];
        this.senderUid = state.fromUid ?? state.teamUid;
      },
      error: (err) =>
        (this.teamChatError =
          err?.error?.error ?? `Could not read the chat: ${err?.message ?? err}`),
    });
  }

  /** An opener with the member's and the sender's first names filled in. */
  private fill(opener: OpenerDto): string {
    return opener.text
      .replace(/\{name\}/g, this.user.firstName?.trim() || 'there')
      .replace(/\{sender\}/g, this.sender?.signsAs || 'Sharon');
  }

  /** Drop the chosen opener into the box, with the names filled in. */
  applyOpener(key: string): void {
    const opener = this.openers.find((o) => o.key === key);
    if (!opener) return;
    this.selectedOpener = key;
    this.messageText = this.fill(opener);
  }

  /* Any edit makes it no longer that opener, so the record says "typed". */
  onMessageEdited(): void {
    const opener = this.openers.find((o) => o.key === this.selectedOpener);
    if (!opener) return;
    if (this.messageText !== this.fill(opener)) this.selectedOpener = null;
  }

  sendTeamMessage(): void {
    const text = this.messageText.trim();
    if (!text || this.sending || !this.senderUid) return;
    this.sending = true;
    this.teamChatError = null;
    this.usersService.sendTeamMessage(this.user.id, text, this.selectedOpener, this.senderUid).subscribe({
      next: (sent) => {
        this.sending = false;
        this.messageText = '';
        this.selectedOpener = null;
        this.sentNotice = sent.isNew
          ? 'Sent. A new chat is open and they have a push notification.'
          : 'Sent into the existing chat. They have a push notification.';
        /* Re-read rather than patch: the counts now include this message, and
           a stale "they have never replied" beside a fresh send reads wrong. */
        this.usersService.getTeamChatState(this.user.id, this.senderUid ?? undefined).subscribe({
          next: (state) => (this.teamChat = state),
          error: () => undefined,
        });
      },
      error: (err) => {
        this.sending = false;
        this.teamChatError =
          err?.error?.error ?? `Could not send: ${err?.message ?? err}`;
      },
    });
  }

  block(): void {
    this.runAction('block', this.accountBlock.block(this.user.id), true, 'Account blocked.');
  }

  unblock(): void {
    this.runAction('unblock', this.accountBlock.unblock(this.user.id), false, 'Account restored.');
  }

  private runAction(
    name: string,
    call: Observable<boolean>,
    blockedAfter: boolean,
    done: string
  ): void {
    if (this.busy) return;
    this.busy = name;
    this.actionError = null;
    this.actionNotice = null;
    call.subscribe({
      next: (ran) => {
        this.busy = null;
        if (!ran) return;
        this.user.blocked = blockedAfter;
        this.actionNotice = done;
      },
      error: (err: any) => {
        this.busy = null;
        this.actionError = `Could not ${name}: ${err?.message ?? err}`;
      },
    });
  }

  ngOnDestroy(): void {
    this.locationSubscription.unsubscribe();
  }

  getfreeForText(value?: number | string): string {
    const freeFor = Number(value);
    if (!freeFor) {
      return 'N/A';
    }

    switch (freeFor) {
      case 1:
        return 'Networking';
      case 2:
        return 'Socializing';
      case 3:
        return 'Learning';
      case 4:
        return 'Investing';
      case 5:
        return 'Fundraising';
      case 6:
        return 'Hiring talents';
      case 7:
        return 'Job opportunity';
      default:
        return 'N/A';
    }
  }

  getTravelFrequencyText(value?: number | null) {
    const travelFreq = Number(value);
    if (!travelFreq) {
      return 'N/A';
    }

    switch (travelFreq) {
      case 1:
        return 'Daily';
      case 2:
        return 'Weekly';
      case 3:
        return 'Monthly';
      case 4:
        return 'Yearly';
      case 5:
        return 'Once in a while';
      default:
        return 'N/A';
    }
  }

  getFromNow(date: string): string {
    return dayjs(date).fromNow();
  }

  getGoogleMapsIframeSrc(): SafeUrl {
    const userLocation = `${this.user.lastKnowLocation?.latitude},${this.user.lastKnowLocation?.longitude}`;

    return this.domSanitizer.bypassSecurityTrustResourceUrl(
      `https://www.google.com/maps/embed/v1/place?key=${environment.firebase.apiKey}&q=${userLocation}&zoom=9`
    );
  }

  onIframeLoad(event: Event) {
    if ((event.target as HTMLIFrameElement).src) {
      this.mapLoaded = true;
    }
  }

  onExernalLinkClick(url: string) {
    window.open(url, '_blank');
  }

  /** The member's part in a space, for the pill's colour and icon. */
  spaceRole(event: PartialEventWithUserInfoDto): 'owner' | 'moderator' | 'member' {
    if (event.isOwner) return 'owner';
    if (event.isModerator) return 'moderator';
    return 'member';
  }

  copyId(): void {
    navigator.clipboard?.writeText(this.user.id).then(
      () => {
        this.idCopied = true;
        setTimeout(() => (this.idCopied = false), 1500);
      },
      () => undefined
    );
  }

  getSpaceChipColor(event: PartialEventWithUserInfoDto): ThemePalette {
    if (event.isOwner) {
      return 'warn';
    }

    if (event.isModerator) {
      return 'primary';
    }

    return undefined;
  }

  getSpaceChipTooltip(event: PartialEventWithUserInfoDto): string {
    if (event.isOwner) {
      return 'Owner';
    }

    if (event.isModerator) {
      return 'Moderator';
    }

    return 'Member';
  }

  getSpaceChipHighlight(event: PartialEventWithUserInfoDto): boolean {
    if (event.isOwner || event.isModerator) {
      return true;
    }

    return false;
  }

  onAvatarClick(userProfileUrl: string) {
    if (!this.userProfileUrlForQRCode) {
      this.userProfileUrlForQRCode = userProfileUrl;
    } else {
      this.userProfileUrlForQRCode = undefined;
    }
  }
}
